import { normalizeRequiredText } from '../member/model';
import { parseIsoDateTime } from '../shared/isoDate';
import type {
  AttendanceCategory,
  AttendanceEvent,
  AttendanceEventMemberGroup,
  AttendanceParticipantSnapshot,
  AttendanceRecord,
  AttendanceRecordStatus,
  Schedule
} from './model';

const DEFAULT_ATTENDANCE_CATEGORY_NAMES = ['例排', '加排'] as const;
const STARTED_SCHEDULE_LOCK_MESSAGE = 'Schedule has already started; attendance and time fields are locked';

export interface ScheduleDraft {
  readonly title: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly location: string;
  readonly note?: string | null;
}

export interface NormalizedScheduleDraft {
  readonly title: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly location: string;
  readonly note: string;
}

export interface CreateAttendanceSettings {
  readonly attendanceEventId: string;
  readonly attendanceCategoryId: string;
  readonly participantMemberGroupIds: readonly string[];
  readonly onTimeReferenceMinutesFromStart: number;
  readonly lateReferenceMinutesFromStart: number;
}

export interface UpdateAttendanceSettings {
  readonly attendanceCategoryId: string;
  readonly participantMemberGroupIds: readonly string[];
  readonly onTimeReferenceMinutesFromStart: number;
  readonly lateReferenceMinutesFromStart: number;
}

export interface ScopedAttendanceMember {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly memberGroupNames: readonly string[];
}

export function buildDefaultAttendanceCategories(input: {
  readonly choirId: string;
  readonly seasonId: string;
  readonly createdAt: string;
  readonly newId: (prefix: 'attendanceCategory') => string;
}): readonly AttendanceCategory[] {
  assertValidIsoDateString(input.createdAt, 'createdAt');

  return DEFAULT_ATTENDANCE_CATEGORY_NAMES.map((name, index) => ({
    attendanceCategoryId: input.newId('attendanceCategory'),
    choirId: input.choirId,
    seasonId: input.seasonId,
    name,
    status: 'enabled',
    displayOrder: index,
    createdAt: input.createdAt,
    updatedAt: input.createdAt
  }));
}

export function normalizeScheduleDraft(input: ScheduleDraft): NormalizedScheduleDraft {
  assertScheduleTimeRange(input.startsAt, input.endsAt);

  return {
    title: normalizeRequiredText(input.title, 'Schedule title'),
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    location: normalizeRequiredText(input.location, 'Schedule location'),
    note: input.note?.trim() ?? ''
  };
}

export function createScheduleEntities(input: {
  readonly choirId: string;
  readonly seasonId: string;
  readonly actorMembershipId: string;
  readonly now: string;
  readonly scheduleId: string;
  readonly draft: ReturnType<typeof normalizeScheduleDraft>;
  readonly attendance: CreateAttendanceSettings | null;
}): {
  readonly schedule: Schedule;
  readonly attendanceEvent: AttendanceEvent | null;
  readonly attendanceEventMemberGroups: readonly AttendanceEventMemberGroup[];
} {
  assertValidIsoDateString(input.now, 'now');
  assertScheduleTimeRange(input.draft.startsAt, input.draft.endsAt);

  const schedule: Schedule = {
    scheduleId: input.scheduleId,
    choirId: input.choirId,
    seasonId: input.seasonId,
    title: input.draft.title,
    startsAt: input.draft.startsAt,
    endsAt: input.draft.endsAt,
    location: input.draft.location,
    note: input.draft.note,
    status: 'active',
    createdByMembershipId: input.actorMembershipId,
    createdAt: input.now,
    updatedAt: input.now,
    deletedAt: null
  };

  if (input.attendance === null) {
    return {
      schedule,
      attendanceEvent: null,
      attendanceEventMemberGroups: []
    };
  }

  validateAttendanceSettings(input.attendance);

  const attendanceEvent: AttendanceEvent = {
    attendanceEventId: input.attendance.attendanceEventId,
    scheduleId: schedule.scheduleId,
    choirId: schedule.choirId,
    seasonId: schedule.seasonId,
    attendanceCategoryId: input.attendance.attendanceCategoryId,
    onTimeReferenceMinutesFromStart: input.attendance.onTimeReferenceMinutesFromStart,
    lateReferenceMinutesFromStart: input.attendance.lateReferenceMinutesFromStart,
    snapshotGeneratedAt: null,
    settledAt: null,
    createdAt: input.now,
    updatedAt: input.now
  };

  return {
    schedule,
    attendanceEvent,
    attendanceEventMemberGroups: buildAttendanceEventMemberGroups(
      attendanceEvent.attendanceEventId,
      input.attendance.participantMemberGroupIds
    )
  };
}

export function applyScheduleUpdate(input: {
  readonly existing: Schedule;
  readonly existingAttendanceEvent: AttendanceEvent | null;
  readonly now: string;
  readonly draft: ReturnType<typeof normalizeScheduleDraft>;
  readonly attendance?: UpdateAttendanceSettings | null;
}): {
  readonly schedule: Schedule;
  readonly canReplaceAttendanceSettings: boolean;
} {
  const nowTime = assertValidIsoDateString(input.now, 'now');
  const existingStartsAtTime = assertValidIsoDateString(input.existing.startsAt, 'existing.startsAt');
  assertScheduleTimeRange(input.draft.startsAt, input.draft.endsAt);

  if (nowTime >= existingStartsAtTime) {
    if (
      input.draft.startsAt !== input.existing.startsAt ||
      input.draft.endsAt !== input.existing.endsAt ||
      input.attendance !== undefined
    ) {
      throw new Error(STARTED_SCHEDULE_LOCK_MESSAGE);
    }

    return {
      schedule: {
        ...input.existing,
        title: input.draft.title,
        location: input.draft.location,
        note: input.draft.note,
        updatedAt: input.now
      },
      canReplaceAttendanceSettings: false
    };
  }

  if (input.attendance !== undefined && input.attendance !== null) {
    validateAttendanceSettings(input.attendance);
  }

  return {
    schedule: {
      ...input.existing,
      title: input.draft.title,
      startsAt: input.draft.startsAt,
      endsAt: input.draft.endsAt,
      location: input.draft.location,
      note: input.draft.note,
      updatedAt: input.now
    },
    canReplaceAttendanceSettings: input.attendance !== undefined
  };
}

export function markScheduleDeleted(input: { readonly existing: Schedule; readonly now: string }): Schedule {
  assertValidIsoDateString(input.now, 'now');

  if (input.existing.status === 'deleted') {
    return input.existing;
  }

  return {
    ...input.existing,
    status: 'deleted',
    updatedAt: input.now,
    deletedAt: input.now
  };
}

export function generateAttendanceSnapshot(input: {
  readonly attendanceEvent: AttendanceEvent;
  readonly now: string;
  readonly scopedMembers: readonly ScopedAttendanceMember[];
  readonly newSnapshotId: () => string;
  readonly newRecordId: () => string;
}): {
  readonly attendanceEvent: AttendanceEvent;
  readonly snapshots: readonly AttendanceParticipantSnapshot[];
  readonly records: readonly AttendanceRecord[];
} {
  assertValidIsoDateString(input.now, 'now');

  if (input.attendanceEvent.snapshotGeneratedAt !== null) {
    return {
      attendanceEvent: input.attendanceEvent,
      snapshots: [],
      records: []
    };
  }

  const scopedMembers = uniqueBySeasonMembershipId(input.scopedMembers);
  const snapshots: AttendanceParticipantSnapshot[] = [];
  const records: AttendanceRecord[] = [];

  for (const scopedMember of scopedMembers) {
    const snapshot: AttendanceParticipantSnapshot = {
      attendanceParticipantSnapshotId: input.newSnapshotId(),
      attendanceEventId: input.attendanceEvent.attendanceEventId,
      seasonMembershipId: scopedMember.seasonMembershipId,
      nicknameSnapshot: scopedMember.nickname,
      memberGroupNamesSnapshot: [...scopedMember.memberGroupNames],
      createdAt: input.now
    };

    snapshots.push(snapshot);
    records.push({
      attendanceRecordId: input.newRecordId(),
      attendanceEventId: input.attendanceEvent.attendanceEventId,
      attendanceParticipantSnapshotId: snapshot.attendanceParticipantSnapshotId,
      seasonMembershipId: scopedMember.seasonMembershipId,
      status: 'pending',
      selectedAt: null,
      settledAt: null,
      updatedAt: input.now
    });
  }

  return {
    attendanceEvent: {
      ...input.attendanceEvent,
      snapshotGeneratedAt: input.now,
      updatedAt: input.now
    },
    snapshots,
    records
  };
}

export function signAttendanceRecord(input: {
  readonly schedule: Schedule;
  readonly attendanceEvent: AttendanceEvent;
  readonly record: AttendanceRecord | null | undefined;
  readonly now: string;
  readonly status: Extract<AttendanceRecordStatus, 'on_time' | 'late'>;
}): AttendanceRecord {
  const nowTime = assertValidIsoDateString(input.now, 'now');
  const startsAtTime = assertValidIsoDateString(input.schedule.startsAt, 'schedule.startsAt');
  const endsAtTime = assertValidIsoDateString(input.schedule.endsAt, 'schedule.endsAt');

  if (input.schedule.status === 'deleted') {
    throw new Error('Deleted schedules cannot accept attendance sign-in');
  }

  if (input.status !== 'on_time' && input.status !== 'late') {
    throw new Error('Attendance sign-in status must be on_time or late');
  }

  if (!input.record) {
    throw new Error('Attendance record is required');
  }

  if (input.record.status === 'absent') {
    throw new Error('Absent attendance records cannot be signed');
  }

  if (input.attendanceEvent.scheduleId !== input.schedule.scheduleId) {
    throw new Error('Attendance event must belong to schedule');
  }

  if (input.record.attendanceEventId !== input.attendanceEvent.attendanceEventId) {
    throw new Error('Attendance record must belong to attendance event');
  }

  if (nowTime < startsAtTime) {
    throw new Error('Attendance sign-in has not started');
  }

  if (nowTime > endsAtTime) {
    throw new Error('Attendance sign-in has ended');
  }

  return {
    ...input.record,
    status: input.status,
    selectedAt: input.now,
    updatedAt: input.now
  };
}

export function settleAttendanceRecords(input: {
  readonly schedule: Schedule;
  readonly attendanceEvent: AttendanceEvent;
  readonly records: readonly AttendanceRecord[];
  readonly now: string;
}): {
  readonly attendanceEvent: AttendanceEvent;
  readonly records: readonly AttendanceRecord[];
} {
  const nowTime = assertValidIsoDateString(input.now, 'now');
  const endsAtTime = assertValidIsoDateString(input.schedule.endsAt, 'schedule.endsAt');

  if (input.attendanceEvent.scheduleId !== input.schedule.scheduleId) {
    throw new Error('Attendance event must belong to schedule');
  }

  if (input.records.some((record) => record.attendanceEventId !== input.attendanceEvent.attendanceEventId)) {
    throw new Error('Attendance records must belong to attendance event');
  }

  if (input.attendanceEvent.settledAt !== null || nowTime <= endsAtTime) {
    return {
      attendanceEvent: input.attendanceEvent,
      records: input.records
    };
  }

  return {
    attendanceEvent: {
      ...input.attendanceEvent,
      settledAt: input.now,
      updatedAt: input.now
    },
    records: input.records.map((record) =>
      record.status === 'pending'
        ? {
            ...record,
            status: 'absent',
            settledAt: input.now,
            updatedAt: input.now
          }
        : record
    )
  };
}

function buildAttendanceEventMemberGroups(
  attendanceEventId: string,
  memberGroupIds: readonly string[]
): readonly AttendanceEventMemberGroup[] {
  return memberGroupIds.map((memberGroupId) => ({
    attendanceEventId,
    memberGroupId
  }));
}

function validateAttendanceSettings(input: {
  readonly onTimeReferenceMinutesFromStart: number;
  readonly lateReferenceMinutesFromStart: number;
  readonly participantMemberGroupIds: readonly string[];
}): void {
  if (
    !Number.isFinite(input.onTimeReferenceMinutesFromStart) ||
    !Number.isFinite(input.lateReferenceMinutesFromStart)
  ) {
    throw new Error('Attendance reference minutes must be finite');
  }

  if (input.onTimeReferenceMinutesFromStart < 0 || input.lateReferenceMinutesFromStart < 0) {
    throw new Error('Attendance reference minutes cannot be negative');
  }

  if (input.lateReferenceMinutesFromStart < input.onTimeReferenceMinutesFromStart) {
    throw new Error('Late reference minutes must be greater than or equal to on-time reference minutes');
  }

  if (new Set(input.participantMemberGroupIds).size !== input.participantMemberGroupIds.length) {
    throw new Error('Attendance scope member groups must be unique');
  }
}

function assertScheduleTimeRange(startsAt: string, endsAt: string): void {
  const startsAtTime = assertValidIsoDateString(startsAt, 'startsAt');
  const endsAtTime = assertValidIsoDateString(endsAt, 'endsAt');

  if (endsAtTime <= startsAtTime) {
    throw new Error('Schedule end time must be after start time');
  }
}

function assertValidIsoDateString(value: string, label: string): number {
  return parseIsoDateTime(value, `${label} must be a valid ISO date string`);
}

function uniqueBySeasonMembershipId(members: readonly ScopedAttendanceMember[]): readonly ScopedAttendanceMember[] {
  const seen = new Set<string>();
  const uniqueMembers: ScopedAttendanceMember[] = [];

  for (const member of members) {
    if (!seen.has(member.seasonMembershipId)) {
      seen.add(member.seasonMembershipId);
      uniqueMembers.push(member);
    }
  }

  return uniqueMembers;
}
